import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

const cardsData = [
  {
    id: 1,
    imgSrc: "/airTicketing2.webp",
    title: "Air Ticketing",
    description:
      "Access global flight options with exclusive corporate fares. We handle your bookings efficiently with our IATA-accredited expertise.",
    fullText:
      "My Ticketer, as an IATA-certified partner, provides streamlined air ticketing services for corporate travelers. From the best fare options to flexible routes and fast confirmations, we ensure every flight aligns with your business goals.",
    gallery: [
      "/airTicketing2.webp",
      "/airTicketing3.webp",
      "/airTicketing.webp",
      "/airTicketing4.webp",
    ],
  },
  {
    id: 2,
    imgSrc: "/visaProcessing.webp",
    title: "Visa Processing",
    description:
      "Comprehensive visa assistance tailored to your destination. We manage the entire process for you—quickly and correctly.",
    fullText:
      "We make visa applications simple. Our team assists with all the paperwork, appointments, and requirements for business or personal travel, ensuring timely approvals and peace of mind for every destination.",
    gallery: [
      "/visaProcessing.webp",
      "/visaProcessing2.webp",
      "/visaProcessing3.webp",
      "/visaProcessing4.webp",
    ],
  },
  {
    id: 3,
    imgSrc: "/travelInsurance.webp",
    title: "Travel Insurance",
    description:
      "Stay covered with custom corporate travel insurance. From delays to emergencies, we’ve got you protected.",
    fullText:
      "Our travel insurance solutions safeguard you against unexpected disruptions. My Ticketer partners with trusted providers to offer full coverage—from trip delays and baggage loss to medical emergencies—so your business never skips a beat.",
    gallery: [
      "/travelInsurance.webp",
      "/travelInsurance2.webp",
      "/travelInsurance3.webp",
      "/travelInsurance4.webp",
    ],
  },
  {
    id: 4,
    imgSrc: "/travelConsultation.webp",
    title: "Travel Consultation",
    description:
      "Personalized travel planning with zero hassle. Let our experts design itineraries that work for your business priorities.",
    fullText:
      "Our consultants provide expert guidance for all your travel needs. Whether it's optimizing routes, choosing compliant carriers, or building multi-city itineraries, we ensure every trip is efficient, cost-effective, and stress-free.",
    gallery: [
      "/travelConsultation.webp",
      "/travelConsultation2.webp",
      "/travelConsultation3.webp",
      "/travelConsultation4.webp",
    ],
  },
];

const CorporateTravels: React.FC = () => {
  const [selectedCard, setSelectedCard] = useState<any | null>(null);

  return (
    <div className="justify-center items-center pb-20">
      {/* Cover Section */}
      <section className="relative w-full h-[70vh] md:h-[80vh] overflow-hidden">
        <img
          src="/corparate.webp"
          alt="Corporate Travel Services"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
        <div className="absolute inset-0 bg-black/40 z-0" />
        <motion.div
          className="relative z-10 flex flex-col items-center text-center md:text-left md:items-end md:flex-row justify-between max-w-7xl mx-auto px-6 md:px-12 h-full pb-28"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 md:mb-0 mt-64 md:mt-0">
            Corporate Travel Services
          </h1>
          <p className="text-white text-md md:text-lg max-w-xl text-center md:text-left">
            Your trusted partner for end-to-end corporate travel — from visas and ticketing to insurance and expert planning,
            we ensure your team travels smoothly and confidently.
          </p>
        </motion.div>
      </section>

      {/* Card Grid */}
      <section className="py-20 px-6 md:px-12 bg-white">
        <h2 className="text-4xl font-bold text-center text-gray-800 mb-6">
          Elevate Your Business Travel Experience
        </h2>
        <p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">
          My Ticketer offers seamless corporate travel solutions — including visa support,
          flight management, insurance, and strategic consulting — for a hassle-free, high-efficiency journey.
        </p>
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {cardsData.map((card) => (
            <div
              key={card.id}
              className="relative rounded-3xl overflow-hidden shadow-lg group cursor-pointer"
              onClick={() => {
                setSelectedCard(card);
              }}
            >
              <img
                src={card.imgSrc}
                alt={card.title}
                loading="lazy"
                className="w-full h-96 object-cover transform group-hover:scale-105 transition duration-500"
              />
              <div className="absolute bottom-0 bg-white p-4 backdrop-blur-sm w-full">
                <p className="text-gray-700 text-sm mb-2">{card.description}</p>
                <h3 className="text-lg font-bold text-gray-900">
                  {card.title}
                </h3>
              </div>
            </div>
          ))}
        </motion.div>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {selectedCard && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedCard(null)}
          >
            <motion.div
              className="bg-white overflow-hidden rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative"
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex justify-center">
                {selectedCard.gallery.map((img: string, i: number) => (
                  <div key={i} className="sm:min-w-[230px] w-1/4 h-96 relative">
                    <img
                      src={img}
                      alt={`thumb-${i}`}
                      loading="lazy"
                      className="w-full h-full object-cover clip-diagonal hover:scale-105 transition duration-500"
                    />
                  </div>
                ))}
              </div>
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-3">
                  {selectedCard.title}
                </h2>
                <p className="text-gray-600">{selectedCard.fullText}</p>
              </div>
              <button
                aria-label="Button"
                onClick={() => setSelectedCard(null)}
                className="absolute cursor-pointer top-3 right-4 text-3xl text-gray-500 hover:text-black"
              >
                &times;
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Call to Action */}
      <motion.div
        className="flex justify-center"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Link
          to="/contact?subject=Corporate%20Travel%20Service%20Request&message=I%20would%20like%20to%20customize%20a%20Corporate%20Travel%20Service.%20Please%20assist..."
          className="bg-amber-400 text-white text-sm font-medium px-6 py-3 rounded-lg hover:bg-amber-500 transition"
        >
          Talk to a Corporate Travel Specialist
        </Link>
      </motion.div>
    </div>
  );
};

export default CorporateTravels;
